/**
 * useKeyboard Hook - Keyboard shortcuts and navigation
 */

import { useState, useEffect, useCallback, useRef } from 'react'

// Keyboard shortcut definitions
export const SHORTCUTS = {
  // Navigation
  DASHBOARD: ['g', 'd'],
  LEGAL: ['g', 'l'],
  INVESTOR: ['g', 'i'],
  SETTINGS: ['g', 's'],
  MESSAGES: ['g', 'm'],
  
  // Actions
  SEARCH: ['ctrl+k', 'cmd+k'],
  SAVE: ['ctrl+s', 'cmd+s'],
  CANCEL: ['esc'],
  DELETE: ['ctrl+backspace', 'cmd+backspace'],
  NEW: ['ctrl+n', 'cmd+n'],
  UNDO: ['ctrl+z', 'cmd+z'],
  REDO: ['ctrl+shift+z', 'cmd+shift+z'],
  
  // Modal
  CLOSE_MODAL: ['esc'],
  CONFIRM: ['enter'],
  
  // Chat
  SEND_MESSAGE: ['ctrl+enter', 'cmd+enter'],
}

// Normalize key combination with null/undefined check
function normalizeKey(key) {
  if (!key || typeof key !== 'string') return ''
  return key.toLowerCase().replace(/^(ctrl|cmd|alt|shift)\+/, '')
}

// Check if event matches shortcut with null/undefined checks
function matchesShortcut(event, shortcut) {
  if (!event || !shortcut) return false
  if (typeof shortcut !== 'string') return false
  
  const keys = shortcut.split('+')
  const hasCtrl = keys.includes('ctrl') && (event.ctrlKey || event.metaKey)
  const hasCmd = keys.includes('cmd') && event.metaKey
  const hasAlt = keys.includes('alt') && event.altKey
  const hasShift = keys.includes('shift') && event.shiftKey
  
  const keyPart = keys.filter(k => !['ctrl', 'cmd', 'alt', 'shift'].includes(k))
  const eventKey = normalizeKey(event.key)
  const matchesKey = keyPart.length === 0 || eventKey === normalizeKey(keyPart[0])
  
  return matchesKey && (hasCtrl || hasCmd || hasAlt || hasShift || keys.length === 1)
}

/**
 * useKeyboardShortcut - Register keyboard shortcuts
 */
export function useKeyboardShortcut(shortcut, callback, options = {}) {
  const { enabled = true, preventDefault = true, eventType = 'keydown' } = options
  const callbackRef = useRef(callback)

  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  useEffect(() => {
    if (!enabled) return

    const handler = (event) => {
      if (!shortcut || typeof shortcut !== 'string') return
      
      if (matchesShortcut(event, shortcut)) {
        if (preventDefault) {
          event.preventDefault()
        }
        callbackRef.current(event)
      }
    }

    document.addEventListener(eventType, handler)
    return () => document.removeEventListener(eventType, handler)
  }, [shortcut, enabled, preventDefault, eventType])
}

/**
 * useKeyboardNavigation - Arrow key navigation for lists
 */
export function useKeyboardNavigation(items = [], onSelect, options = {}) {
  const { loop = false } = options
  const [selectedIndex, setSelectedIndex] = useState(-1)

  const navigate = useCallback((direction) => {
    setSelectedIndex((prev) => {
      const newIndex = prev + direction
      if (loop) {
        if (newIndex < 0) return items.length - 1
        if (newIndex >= items.length) return 0
        return newIndex
      }
      return Math.max(0, Math.min(newIndex, items.length - 1))
    })
  }, [items.length, loop])

  const reset = useCallback(() => {
    setSelectedIndex(-1)
  }, [])

  useKeyboardShortcut('ArrowDown', () => navigate(1), { enabled: items.length > 0 })
  useKeyboardShortcut('ArrowUp', () => navigate(-1), { enabled: items.length > 0 })

  useKeyboardShortcut('Enter', () => {
    if (selectedIndex >= 0 && selectedIndex < items.length) {
      onSelect?.(items[selectedIndex], selectedIndex)
    }
  }, { enabled: selectedIndex >= 0 })

  return { selectedIndex, selectedItem: items[selectedIndex] || null, navigate, reset }
}

/**
 * useKeyboardFocus - Manage focus within a container
 */
export function useKeyboardFocus() {
  const containerRef = useRef(null)

  const focusFirst = useCallback(() => {
    const container = containerRef.current
    if (!container) return

    const focusable = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
    if (focusable.length > 0) {
      focusable[0].focus()
    }
  }, [])

  const focusLast = useCallback(() => {
    const container = containerRef.current
    if (!container) return

    const focusable = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
    if (focusable.length > 0) {
      focusable[focusable.length - 1].focus()
    }
  }, [])

  return { containerRef, focusFirst, focusLast }
}

/**
 * useEscape - Handle escape key
 */
export function useEscape(callback, enabled = true) {
  useKeyboardShortcut('esc', callback, { enabled })
}

/**
 * useCommand - Handle command/ctrl key shortcuts
 */
export function useCommand(shortcut, callback, enabled = true) {
  const fullShortcut = shortcut.startsWith('ctrl+') || shortcut.startsWith('cmd+')
    ? shortcut
    : `cmd+${shortcut}`
  
  useKeyboardShortcut(fullShortcut, callback, { enabled })
}

export default {
  SHORTCUTS,
  useKeyboardShortcut,
  useKeyboardNavigation,
  useKeyboardFocus,
  useEscape,
  useCommand,
}